"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useResource = void 0;
const ketting_1 = require("ketting");
const react_1 = require("react");
const use_client_1 = require("./use-client");
function useResource(arg1) {
    // Here be dragons
    const [resourceLike, mode, initialData, refreshOnStale] = getUseResourceOptions(arg1);
    const [resource, setResource] = react_1.useState(resourceLike instanceof ketting_1.Resource ? resourceLike : undefined);
    const client = use_client_1.useClient();
    const [resourceState, setResourceState] = useResourceState(resourceLike, initialData, client);
    const [loading, setLoading] = react_1.useState(resourceState === undefined);
    const [error, setError] = react_1.useState(null);
    const [modeVal, setModeVal] = react_1.useState(mode);
    react_1.useEffect(() => {
        // This effect is for finding the real Resource object
        if (resourceLike instanceof ketting_1.Resource) {
            setResource(resourceLike);
        }
        else if (typeof resourceLike === 'string') {
            setResource(client.go(resourceLike));
        }
        else {
            Promise.resolve(resourceLike).then(newRes => {
                setResource(newRes);
            }).catch(err => {
                setError(err);
                setLoading(false);
            });
        }
    }, [resourceLike]);
    react_1.useEffect(() => {
        // This effect is for setting up the onUpdate event
        if (!resource || mode === 'POST') {
            return;
        }
        const onUpdate = (newState) => {
            setResourceState(newState.clone());
            setLoading(false);
        };
        const onStale = () => {
            if (refreshOnStale) {
                resource
                    .refresh()
                    .catch(err => {
                    setError(err);
                });
            }
        };
        resource.on('update', onUpdate);
        resource.on('stale', onStale);
        return function unmount() {
            resource.off('update', onUpdate);
            resource.off('stale', onStale);
        };
    }, [resource]);
    react_1.useEffect(() => {
        // This effect is for fetching the initial ResourceState
        if (!resource || modeVal === 'POST') {
            // No need to fetch resourceState for these cases.
            return;
        }
        if (resourceState && resourceState.uri === resource.uri) {
            // Don't do anything if we already have a resourceState, and the
            // resourceState's uri matches what we got.
            //
            // This likely means we got the resourceState from the initial
            // useResourceState hook.
            return;
        }
        // The 'resource' property has changed, so lets get the new resourceState and data.
        const cachedState = resource.client.cache.get(resource.uri);
        if (cachedState) {
            setResourceState(cachedState);
            setLoading(false);
            return;
        }
        else {
            setResourceState(undefined);
            setLoading(true);
        }
        resource.get()
            .then(newState => {
            setResourceState(newState.clone());
            setLoading(false);
        })
            .catch(err => {
            setError(err);
            setLoading(false);
        });
    }, [resource]);
    const result = {
        loading,
        error,
        resourceState: resourceState,
        setResourceState: (newState) => {
            if (!resource) {
                throw new Error('Too early to call setResourceState, we don\'t have a current state to update');
            }
            if (modeVal === 'POST') {
                setResourceState(newState);
            }
            else {
                resource.updateCache(newState);
            }
        },
        resource: resource,
        submit: async () => {
            if (!resourceState || !resource) {
                throw new Error('Too early to call submit()');
            }
            if (modeVal === 'POST') {
                const newResource = await resource.postFollow(resourceState);
                setResource(newResource);
                setModeVal('PUT');
            }
            else {
                await resource.put(resourceState);
            }
        },
        data: (resourceState === null || resourceState === void 0 ? void 0 : resourceState.data),
        setData: (data) => {
            if (!resourceState || !resource) {
                throw new Error('Too early to call setData, we don\'t have a current state to update');
            }
            resourceState.data = data;
            if (modeVal === 'POST') {
                setResourceState(resourceState);
            }
            else {
                resource.updateCache(resourceState);
            }
        }
    };
    return result;
}
exports.useResource = useResource;
/**
 * A helper function to process the overloaded arguments of useResource, and return a consistent result
 */
function getUseResourceOptions(arg1) {
    var _a;
    const client = use_client_1.useClient();
    let mode;
    let initialState;
    let res;
    let refreshOnStale;
    if (isUseResourceOptions(arg1)) {
        mode = arg1.mode;
        initialState = arg1.initialState;
        res = arg1.resource;
        refreshOnStale = (_a = arg1.refreshOnStale) !== null && _a !== void 0 ? _a : false;
    }
    else {
        mode = 'PUT';
        initialState = undefined;
        res = arg1;
        refreshOnStale = false;
    }
    return [
        typeof res === 'string' ? client.go(res) : res,
        mode,
        initialState,
        refreshOnStale,
    ];
}
/**
 * Internal helper hook to deal with setting up the resource state, and
 * populate the cache.
 */
function useResourceState(resource, initialData, client) {
    let data = undefined;
    if (initialData) {
        data = ketting_1.isState(initialData) ? initialData : dataToState(initialData, client);
    }
    else if (resource instanceof ketting_1.Resource) {
        data = client.cache.get(resource.uri) || undefined;
    }
    const [resourceState, setResourceState] = react_1.useState(data);
    return [resourceState, setResourceState];
}
function isUseResourceOptions(input) {
    return input.mode === 'PUT' || input.mode === 'POST';
}
/**
 * Take data and wraps it in a State object.
 *
 * For now this will always return a HalState object, because it's a
 * reasonable default, but this may change in the future.
 */
function dataToState(data, client) {
    return new ketting_1.HalState({
        uri: 'about:blank' + Math.random(),
        client,
        data,
        headers: new Headers(),
        links: new ketting_1.Links('about:blank'),
    });
}
//# sourceMappingURL=use-resource.js.map